import { normalize } from '@sentry/utils';
import debug from 'debug';
import ENVIRONMENT, { Environment } from '../environment';
import { Tag } from './constants';
import { Sentry } from './sentry';
import { flattenObj, objectifyError } from '../utils';

const isDebug = () => {
  if ([Environment.Development, Environment.Staging, Environment.Production].includes(ENVIRONMENT)) {
    return false;
  }

  return true;
};

const createSentryLogger = (appName, feature) => {
  const namespace = feature ? `${appName}:${feature}` : appName;
  return {
    log: (...message) => Sentry.addBreadcrumb({
      category: namespace,
      message: message.join('\n'),
      level: 'info'
    }),
    submit: (...message) => {
      Sentry.withScope(scope => {
        scope.setTag(Tag.AppName, appName);
        scope.setTag(Tag.Feature, feature || '-');
        Sentry.captureMessage(`${namespace} - ${message.join('\n')}`);
      });
    },
    raise: error => {
      Sentry.withScope(scope => {
        const errorObj = flattenObj(objectifyError(error));

        for (let key in errorObj) {
          if (key !== 'stack') {
            scope.setExtra(`error.${key}`, normalize(errorObj[key]));
          }
        }

        scope.setTag(Tag.AppName, appName);
        scope.setTag(Tag.Feature, feature || '-');
        Sentry.captureException(error);
      });
    }
  };
};

const createDebugLogger = (appName, feature) => {
  let logger = debug('bt').extend(appName);

  if (feature) {
    logger = logger.extend(feature);
  }

  return {
    log: logger,
    submit: logger,
    raise: logger
  };
};

const createLogger = (appName, feature) => {
  if (isDebug()) {
    return createDebugLogger(appName, feature);
  }

  return createSentryLogger(appName, feature);
};

const createLoggerGetter = appName => {
  return feature => createLogger(appName, feature);
};

export { createLogger, createLoggerGetter, createDebugLogger, createSentryLogger };